import React from "react"

import classNames from "classnames"

import { deepMap } from "shared/lib/arrayUtils"
import { times } from "shared/lib/times"
import { useValueFormatter } from "shared/lib/useBooleanValue"
import { NestedEntries } from "shared/types/Entry"
import { Maybe } from "shared/types/Maybe"
import { Primitive } from "shared/types/Primitive"
import { Chip } from "shared/ui/chip/Chip"
import { Readonly } from "shared/ui/readonly/Readonly"

import "./styles/_property-list.scss"

export interface PropertyListProps {
  list: NestedEntries<Maybe<Primitive>, Maybe<Primitive>> | null
  isLoading?: boolean
  loadingEntries?: number
  className?: string
  hideChip?: boolean
}

export const PropertyList = React.memo(
  ({
    list,
    isLoading,
    hideChip,
    className,
    loadingEntries = 16
  }: PropertyListProps) => {
    const formatValue = useValueFormatter()
    return (
      <div
        className={classNames(
          "property-list",
          isLoading ? "loading" : undefined,
          className
        )}
        data-testid="property-list"
      >
        {isLoading
          ? times(loadingEntries).map(index => (
              <div
                key={index}
                className="property-list__entry"
                data-testid="property-list-entry"
              >
                {!hideChip ? (
                  <Chip className="property-list__chip" isLoading />
                ) : null}
                <Readonly.Root isLoading>
                  <Readonly.Label isLoading />
                  <Readonly.Value />
                </Readonly.Root>
              </div>
            ))
          : null}
        {!isLoading && list
          ? deepMap(list, entry => (
              <div
                key={entry.label}
                className="property-list__entry"
                data-testid="property-list-entry"
              >
                {!hideChip ? (
                  <Chip className="property-list__chip">
                    {formatValue(entry.valueShort)}
                  </Chip>
                ) : null}
                <Readonly.Root>
                  <Readonly.Label>{entry.label}</Readonly.Label>
                  <Readonly.Value>
                    <div className="property-list__value">
                      {formatValue(entry.value)}
                    </div>
                  </Readonly.Value>
                </Readonly.Root>
              </div>
            ))
          : null}
      </div>
    )
  }
)
